import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Skeleton, Typography } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import RoundedBox from '@sharedCR/common/RoundedBox';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import { getPascalCase } from 'app/pages/loanApplication/LoanApplication.utils';
import { NoAvailableMessageContent } from 'app/shared/components/common/NoAvailableMessage';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { NON_BREAKING_SPACE } from 'constants/ui';
import useModifiedFieldPendingChangeAuthors from 'hooks/useModifiedFieldPendingChangeAuthors';

import {
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLockAndLoanInfoSummary,
  selectPricingStatus,
} from 'store/loanApplications/selectors';

import { getSummaryConfig } from './Summary.config';
import {
  getProgramOptions,
  getVisibleSummaryCardsOptions,
  loanDetailsOptions,
  SUMMARY_SKELETONS_LENGTH,
  SummaryCardsName,
} from './Summary.constants';
import { getSummaryCardStyles } from './Summary.styles';

const Summary = ({ isLoading }) => {
  const [activeCardName, setActiveCardName] = useState(SummaryCardsName.PROGRAM);

  const lockAndLoanInfo = useSelector(selectLockAndLoanInfoSummary);
  const pricingStatus = useSelector(selectPricingStatus);
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const getModifiedFieldAuthors = useModifiedFieldPendingChangeAuthors();

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  const summaryConfig = getSummaryConfig({
    ...lockAndLoanInfo,
    pricingStatus,
    isUnsecured,
    isChattel,
  });

  const activeOptionValue = getVisibleSummaryCardsOptions({ isUnsecured, isChattel }).find(
    option => option.name === activeCardName,
  )?.value;
  const activeCardValues = lockAndLoanInfo[activeOptionValue] || {};
  const activeCardOptions =
    activeOptionValue === 'program'
      ? getProgramOptions(activeCardValues, { isUnsecured })
      : loanDetailsOptions[activeOptionValue];

  const onCardClick = useCallback(e => {
    const cardName = e.currentTarget.getAttribute('data-card-name');

    setActiveCardName(cardName);
  }, []);

  return (
    <div className="flex gap-12">
      <div className="flex-1 flex flex-col gap-8 overflow-hidden">
        {isLoading
          ? Array.from({ length: SUMMARY_SKELETONS_LENGTH }).map((_unused, index) => (
              <Box key={index} sx={getSummaryCardStyles({ isClickable: false, isNotActive: true })}>
                <DataDisplay isLoading title="" value="" variant={dataDisplayVariant} />
              </Box>
            ))
          : summaryConfig.map(
              ({
                name,
                value,
                icon,
                iconColor,
                changeKind,
                modifiedFieldDetails,
                pendingChangesKey,
                isClickable = false,
                hoverContent,
              }) => {
                const isNotActive = activeCardName !== name;
                const card = (
                  <Box
                    data-card-name={name}
                    sx={getSummaryCardStyles({ isClickable, isNotActive })}
                    onClick={isClickable ? onCardClick : undefined}
                  >
                    <DataDisplay
                      changeKind={changeKind}
                      icon={icon}
                      iconColor={iconColor}
                      isAdditionalIcon={isClickable}
                      pendingChangeAuthors={getModifiedFieldAuthors(
                        modifiedFieldDetails,
                        pendingChangesKey,
                      )}
                      title={name}
                      value={value}
                      variant={dataDisplayVariant}
                    />
                  </Box>
                );

                return hoverContent ? (
                  <TooltipWrapper key={name} title={hoverContent}>
                    {card}
                  </TooltipWrapper>
                ) : (
                  <React.Fragment key={name}>{card}</React.Fragment>
                );
              },
            )}
      </div>
      <div className="flex-1 flex flex-col gap-8">
        <RoundedBox padding={8}>
          {activeCardOptions ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
              {activeCardOptions.map(({ name, key }, index) => (
                <div className="flex flex-col gap-4" key={index}>
                  {isLoading ? (
                    <Skeleton height={50} variant="text" />
                  ) : (
                    <>
                      <Typography color="text.secondary" variant="regular14">
                        {name}
                      </Typography>
                      <PendingChangeWrapper
                        isSmall
                        authors={getModifiedFieldAuthors(
                          activeCardValues?.modifiedFieldDetails,
                          getPascalCase(key),
                        )}
                        fieldLabel={name}
                        pendingChangesActionType={
                          activeCardValues?.modifiedFields?.includes(getPascalCase(key))
                            ? PendingChangesActionType.MODIFIED
                            : undefined
                        }
                        textSelector="span"
                      >
                        <Typography variant="bold16">
                          {activeCardValues[key] ?? NON_BREAKING_SPACE}
                        </Typography>
                      </PendingChangeWrapper>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <NoAvailableMessageContent delay={0} message="Sorry but there is no data" />
            </div>
          )}
        </RoundedBox>
      </div>
    </div>
  );
};

Summary.propTypes = {
  ...WithLoadingPropTypes,
};

export default Summary;
